import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { UPasswordInput } from 'components/core/u-password-input';
import {
  SharedPasswordStrengthChecker,
} from '../../core/shared-password-strength-checker';
import { Captcha } from 'features/captcha';
import { useConfig } from 'hooks/use-config';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { useCaptchaConfig } from 'features/auth/hooks/use-captcha-config';

/**
 * BasePasswordForm Component
 *
 * A form component for password creation/reset that includes validation, strength checking,
 * and optional CAPTCHA verification for enhanced security.
 *
 * Features:
 * - Password and confirmation fields with validation
 * - Password strength requirements checker
 * - Optional Google reCAPTCHA integration
 * - Form state management using React Hook Form
 * - Zod schema validation
 * - Loading state handling
 * - Navigation after successful submission
 *
 * Props:
 * @param {string} code - Verification code used for the password operation
 * @param {(password: string, code: string, captchaToken?: string) => Promise<void>} onSubmit - Callback for form submission
 * @param {z.ZodSchema} validationSchema - Zod schema for form validation
 * @param {{password: string, confirmPassword: string}} defaultValues - Default values for the form fields
 * @param {boolean} isPending - Loading state flag for form submission
 * @param {boolean} [isCaptchaValid] - Optional flag indicating if CAPTCHA is valid
 * @param {(isValid: boolean) => void} [onCaptchaValidation] - Optional callback when CAPTCHA validation state changes
 *
 * @returns {JSX.Element} A password form with validation and optional CAPTCHA
 *
 * @example
 * // Basic usage
 * <BasePasswordForm
 *   code="reset123"
 *   onSubmit={handlePasswordReset}
 *   validationSchema={passwordSchema}
 *   defaultValues={{ password: "", confirmPassword: "" }}
 *   isPending={isSubmitting}
 *   onCaptchaValidation={setIsCaptchaValid}
 * />
 */

interface BasePasswordFormProps {
  code: string;
  onSubmit: (firstName: string, lastName: string, password: string, code: string, captchaToken?: string) => Promise<void>;
  validationSchema: z.ZodSchema;
  defaultValues: {
    firstName: string;
    lastName: string;
    password: string;
    confirmPassword: string;
  };
  isPending: boolean;
  isCaptchaValid?: boolean;
  onCaptchaValidation?: (isValid: boolean) => void;
  showNameFields?: boolean;
}

export const BasePasswordForm: React.FC<BasePasswordFormProps> = ({
  code,
  onSubmit,
  validationSchema,
  defaultValues,
  isPending,
  onCaptchaValidation,
  showNameFields = true,
}) => {
  const navigate = useNavigate();
  const [requirementsMet, setRequirementsMet] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const { configurationSettings } = useConfig();
  const { data: captchaConfig, isLoading: isCaptchaConfigLoading } = useCaptchaConfig();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const captchaTheme = theme === 'dark' ? 'dark' : 'light';
  const form = useForm({
    defaultValues,
    resolver: zodResolver(validationSchema),
  });

  const password = form.watch('password');
  const confirmPassword = form.watch('confirmPassword');

  const isCaptchaEnabled = Boolean(
    captchaConfig?.captcha && Object.keys(captchaConfig.captcha).length > 0
  );

  useEffect(() => {
    if (
      isCaptchaEnabled &&
      requirementsMet &&
      password &&
      confirmPassword &&
      password === confirmPassword
    ) {
      setShowCaptcha(true);
    } else {
      setShowCaptcha(false);
      if (captchaToken) {
        setCaptchaToken('');
        if (onCaptchaValidation) {
          onCaptchaValidation(false);
        }
      }
    }
  }, [isCaptchaEnabled, requirementsMet, password, confirmPassword, captchaToken, onCaptchaValidation]);

  const handleCaptchaVerify = (token: React.SetStateAction<string>) => {
    setCaptchaToken(token);
    if (onCaptchaValidation) {
      onCaptchaValidation(!!token);
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
    if (onCaptchaValidation) {
      onCaptchaValidation(false);
    }
  };

  const onSubmitHandler = async (values: { firstName: string; lastName: string; password: string; confirmPassword: string }) => {
    if (isCaptchaEnabled && !captchaToken) {
      return;
    }

    try {
      await onSubmit(values.firstName, values.lastName, values.password, code, captchaToken);
      navigate('/success');
    } catch (_error) {
      // Handle error if needed
    }
  };

  const isSubmitDisabled =
    isPending ||
    isCaptchaConfigLoading ||
    !requirementsMet ||
    (isCaptchaEnabled && !captchaToken);

  return (
    <div className="w-full">
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmitHandler)}>
          {showNameFields && (
            <>
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">{t('FIRST_NAME')}</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder={t('ENTER_YOUR_FIRST_NAME')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">{t('LAST_NAME')}</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder={t('ENTER_YOUR_LAST_NAME')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('PASSWORD')}</FormLabel>
                <FormControl>
                  <UPasswordInput placeholder={t('ENTER_YOUR_PASSWORD', 'Enter your password')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">{t('CONFIRM_PASSWORD')}</FormLabel>
                <FormControl>
                  <UPasswordInput placeholder={t('CONFIRM_YOUR_PASSWORD', 'Confirm your password')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <SharedPasswordStrengthChecker
            password={password}
            confirmPassword={confirmPassword}
            onRequirementsMet={setRequirementsMet}
          />

          {showCaptcha && captchaConfig?.captcha && (
            <div className="my-4">
              <Captcha
                type="reCaptcha"
                siteKey={captchaConfig.captcha.key}
                theme={captchaTheme}
                onVerify={handleCaptchaVerify}
                onExpired={handleCaptchaExpired}
                size="normal"
              />
            </div>
          )}

          <Button
            className={`mt-5 w-full bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
            type="submit"
            loading={isPending}
            disabled={isSubmitDisabled}
          >
            {t('CONFIRM')}
          </Button>
        </form>
      </Form>
    </div>
  );
};
